import { memo } from "react";
import { Ban } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { formatCurrency } from "@/shared/lib/formatters";
import type { GraduationResponseDTO } from "../types/graduation.types";

interface GraduationCancelDialogProps {
  open: boolean;
  loading: boolean;
  graduation: GraduationResponseDTO | null;
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * Dialog de confirmação de cancelamento de formatura.
 *
 * O backend faz soft delete da formatura E cancela a nota fiscal
 * vinculada de forma assíncrona. Use `GraduationDeleteDialog` quando
 * a NF deve ficar intacta.
 *
 * @example
 * <GraduationCancelDialog
 *   open={dialogMode === 'cancel'}
 *   loading={dialogLoading}
 *   graduation={selectedGraduation}
 *   onClose={closeDialog}
 *   onConfirm={handleCancelConfirm}
 * />
 */
export const GraduationCancelDialog = memo(function GraduationCancelDialog({
  open,
  loading,
  graduation,
  onClose,
  onConfirm,
}: GraduationCancelDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Cancelar Formatura</DialogTitle>
          <DialogDescription>
            Confirmar o cancelamento desta formatura?
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div className="p-4 bg-amber-500/10 rounded-md border border-amber-500/20">
            <div className="flex items-start gap-3">
              <Ban className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">{graduation?.eventName}</p>
                {graduation ? (
                  <p className="text-sm text-muted-foreground">
                    Valor: {formatCurrency(graduation.totalValue)}
                  </p>
                ) : null}
                <p className="text-sm text-muted-foreground mt-2">
                  A formatura sai da listagem e a nota fiscal vinculada será
                  cancelada de forma assíncrona pelo backend.
                </p>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Voltar
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={loading}>
            {loading ? "Cancelando..." : "Cancelar Formatura"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
